import { useCurrentShopSuspense } from "@/api/shops/use-current-shop-suspense";
import { LockedOverlay } from "@/components/plan-mini/locked-overlay";
import useSizingDiagnostic from "@babackapp/private-api-client/client/use-sizing-diagnostic";
import useTranslation from "@babackapp/translations/use-translation";
import { DateRangePicker } from "@babackapp/ui/components/date-range-picker";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@babackapp/ui/components/card";
import { Input } from "@babackapp/ui/components/input";
import { Skeleton } from "@babackapp/ui/components/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@babackapp/ui/components/table";
import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  type SortingState,
  useReactTable,
} from "@tanstack/react-table";
import { subMonths } from "date-fns";
import { Search } from "lucide-react";
import { useMemo, useState } from "react";
import { MOCK_DIAGNOSTIC, USE_MOCK_DATA } from "../lib/mock-data";
import { SizingExportButton } from "./sizing-export-button";
import { useSizingTableColumns } from "./sizing-table-columns";

type SizingDiagnosticTableProps = {
  isLocked?: boolean;
};

const globalFilterFn = <T extends { productTitle: string }>(
  row: { original: T },
  _columnId: string,
  filterValue: string,
) =>
  row.original.productTitle
    .toLowerCase()
    .includes(filterValue.toLowerCase());

export function SizingDiagnosticTable({ isLocked = false }: SizingDiagnosticTableProps) {
  const { data: shop } = useCurrentShopSuspense();
  const t = useTranslation();
  const columns = useSizingTableColumns();
  const [sorting, setSorting] = useState<SortingState>([
    { id: "sampleSize", desc: true },
  ]);
  const [search, setSearch] = useState("");
  const [dateRange, setDateRange] = useState(() => {
    const now = Date.now();
    return { from: subMonths(now, 3).getTime(), to: now };
  });

  const apiResult = useSizingDiagnostic({
    shopId: shop.id,
    fromTimestamp: dateRange.from,
    toTimestamp: dateRange.to,
  });

  const rawData = isLocked || USE_MOCK_DATA ? MOCK_DIAGNOSTIC : apiResult.data;
  const isLoading = isLocked || USE_MOCK_DATA ? false : apiResult.isLoading;

  const data = useMemo(() => rawData ?? [], [rawData]);

  const table = useReactTable({
    data,
    columns,
    state: { sorting, globalFilter: search },
    onSortingChange: setSorting,
    onGlobalFilterChange: setSearch,
    globalFilterFn,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  });

  const rows = table.getRowModel().rows;

  return (
    <Card className="col-span-4 relative overflow-hidden">
      <CardHeader className="flex-row items-center justify-between space-y-0 gap-4">
        <CardTitle className="text-md shrink-0">{t("sizing.table.title")}</CardTitle>
        <div className="flex items-center gap-2 ml-auto">
          {!isLoading && rows.length > 0 && (
            <span className="text-sm text-muted-foreground tabular-nums">
              {t("sizing.table.productCount", { count: rows.length })}
            </span>
          )}
          {!isLocked && (
            <>
              <DateRangePicker
                className="w-auto"
                range={{ from: dateRange.from, to: dateRange.to }}
                onSelect={(range) => {
                  if (range.from && range.to) {
                    setDateRange({ from: range.from, to: range.to });
                  }
                }}
                maxDate={new Date()}
                closeOnSelect
              />
              <SizingExportButton
                fromTimestamp={dateRange.from}
                toTimestamp={dateRange.to}
              />
            </>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <div className="relative mb-4 max-w-sm">
          <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t("sizing.table.searchPlaceholder")}
            className="pl-8"
            disabled={isLocked}
          />
        </div>
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead key={header.id}>
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {isLoading ? (
              Array.from({ length: 8 }).map((_, i) => (
                <TableRow key={`skeleton-${i}`}>
                  {columns.map((_, j) => (
                    <TableCell key={`skeleton-${i}-${j}`} className="py-2">
                      <Skeleton className="h-4 w-full" />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : rows.length > 0 ? (
              rows.map((row) => (
                <TableRow key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id} className="py-2">
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center text-muted-foreground"
                >
                  {t("sidebar.noResult")}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </CardContent>
      {isLocked && (
        <LockedOverlay
          title="Données disponibles avec le Sizing"
          description="Vos retours sont déjà analysés. Activez le Sizing pour voir quels produits taillent grand ou petit et réduire vos retours taille."
        />
      )}
    </Card>
  );
}
